'use client';

import { useTabs } from 'minimal-shared/hooks';
import { varAlpha } from 'minimal-shared/utils';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';

import { paths, newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useGetReviews } from 'src/actions/reviews';

import { DetailsReview } from 'src/components/reviews';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { useCheckoutContext } from '../../checkout/context';
import { ProductDetailsSummary } from '../product-details-summary';
import { ProductDetailsCarousel } from '../product-details-carousel';
import { ProductDetailsDescription } from '../product-details-description';

// ----------------------------------------------------------------------

const collection = 'promotions';

export function ProductShopDetailsView({ product }) {
  const { t } = useTranslate();

  const tabs = useTabs('description');

  const { state: checkout } = useCheckoutContext();

  const reviewHook = useGetReviews(product?.id, collection);

  return (
    <Container sx={{ mt: 5, mb: 12 }}>
      <CustomBreadcrumbs
        links={[
          { name: t('Categories'), href: `/${newSearchParams({ country: product?.country })}` },
          {
            name: t(product?.category),
            href: `${paths.shop}${newSearchParams({ country: product?.country, category: product?.category })}`,
          },
          { name: product?.name },
        ]}
        sx={{ mb: 3 }}
      />

      <Grid container spacing={{ xs: 3, md: 5, lg: 8 }}>
        <Grid size={{ xs: 12, md: 6, lg: 7 }}>
          <ProductDetailsCarousel slides={product?.images} />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 5 }}>
          {product && (
            <ProductDetailsSummary
              product={product}
              items={checkout.items}
              reviewHook={reviewHook}
              onAddToCart={checkout.onAddToCart}
              disableActions={!product?.available}
            />
          )}
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <Tabs
          value={tabs.value}
          onChange={tabs.onChange}
          sx={[
            (theme) => ({
              px: 3,
              boxShadow: `inset 0 -2px 0 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
            }),
          ]}
        >
          {[
            { value: 'description', label: t('Description') },
            { value: 'reviews', label: `${t('Reviews')} (${reviewHook.totalReviews})` },
          ].map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {tabs.value === 'description' && (
          <ProductDetailsDescription description={product?.description} />
        )}

        {product?.id && (
          <DetailsReview
            id={product?.id}
            collection={collection}
            reviewHook={reviewHook}
            sx={{ ...(tabs.value === 'description' && { height: 0, overflow: 'hidden' }) }}
          />
        )}
      </Card>
    </Container>
  );
}
